import type { Map as MlMap } from "maplibre-gl";
import { useEffect, useRef } from "react";
import { loadMapLibre, MAP_STYLE } from "../../lib/maplibre.ts";

/**
 * Where one recording was taken, as a map you look at rather than one you use.
 *
 * Interaction is off entirely — no drag, no pinch, no rotate — because this is a picture of a
 * place, not a map to explore. The map a traveller explores is a whole screen away and knows
 * about every other recording and the route between them; a map inside a sheet that can be
 * panned is only a map you can get lost in and then have to find your way back out of.
 *
 * The place name lives on the sheet's own line of facts, which is also the switch, so this is
 * only ever the picture half. Nothing is created until it is asked for: opening a photo does not
 * pay for a map nobody wanted to see.
 */
export function PlaceMap({
  lat,
  lon,
  /** The name of the place, when the worker has found one. */
  name,
  /** Set when the point was matched against the route rather than read from the file. */
  estimated,
}: {
  lat: number;
  lon: number;
  name: string | null;
  estimated: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);

  useEffect(() => {
    if (!box.current || map.current) return;
    let live = true;
    void loadMapLibre().then((ml) => {
      // The sheet may have been shut again while half a megabyte of map was on its way.
      if (!live || !box.current || map.current) return;
      const m = new ml.Map({
        container: box.current,
        style: MAP_STYLE,
        center: [lon, lat],
        // Close enough to recognise the street pattern, wide enough to know which town it is.
        zoom: 14,
        interactive: false,
        attributionControl: { compact: true },
      });
      new ml.Marker({ color: estimated ? "#64748b" : "#2563eb" }).setLngLat([lon, lat]).addTo(m);
      map.current = m;
    });
    return () => {
      live = false;
      map.current?.remove();
      map.current = null;
    };
  }, [lat, lon, estimated]);

  // It is laid out while the sheet is still reflowing around it, so it measures a box that is
  // still growing. One resize once that has stopped is cheaper than watching every frame.
  useEffect(() => {
    const t = setTimeout(() => map.current?.resize(), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="place-map"
      ref={box}
      role="img"
      aria-label={`Map of ${name ?? "where this was taken"}`}
    />
  );
}
